// domain/model/EffectivePeriod.js
import { isActiveAsOf, toAsOfDate, toDateStr } from '../services/effectiveDating';

/**
 * EffectivePeriod — the span of calendar dates over which a record is in force.
 *
 * A VALUE OBJECT: no identity, immutable, equal to another period when both bounds match. Two
 * coverages running 2026-01-01 → open are not "the same coverage", but they do hold the same
 * period, and nothing anywhere needs to tell one period instance from another.
 *
 * Named for what it IS, not for what holds it. Coverage is its first holder, but a period is not a
 * coverage concept — authorizations and fee schedules are effective-dated the same way, and the
 * billing projector runs this same predicate to decide which coverage applied on a service date.
 * `EffectiveCoveragePeriod` would also collide with payer usage, where "coverage period" means the
 * benefit year rather than "when this record is in force".
 *
 * WHY THIS EXISTS AS A TYPE. The two bounds were previously two loose strings on Coverage, and
 * four places had to know they were one thing: the entity normalized them separately, the overlap
 * check hand-inlined range intersection, validation re-derived both to compare them, and the
 * status rule passed them positionally to `isActiveAsOf(asOf, start, end)`. Range algebra spread
 * across an entity and three services is the classic shape of a missing value object. Holding them
 * together also makes the invariant structural: there is no longer a way to move one bound and
 * leave the other behind.
 *
 * STORAGE IS UNAFFECTED. `clientCoverages` documents keep two flat `effectiveStart` /
 * `effectiveEnd` strings; `coverageMapper` is where the nested domain shape and the flat document
 * shape meet, exactly as it already reconciles `clientId` with `client`. The projector reads the
 * document, not this type.
 *
 * Bounds are inclusive at both ends, and `null` means open — an open start is "has always
 * applied", an open end is "ongoing". Comparison is lexicographic on 'YYYY-MM-DD', which for
 * zero-padded dates is chronological; see `effectiveDating` for why these are calendar dates
 * rather than instants.
 *
 * @domainObject value-object
 * @invariant Both bounds are 'YYYY-MM-DD' calendar dates or null; an instant is never one.
 * @invariant The end may not precede the start.
 * @invariant Frozen — a period is replaced, never edited in place.
 * @typedef {Object} EffectivePeriod
 * @property {string|null} start - 'YYYY-MM-DD'; null = open (no start bound)
 * @property {string|null} end - 'YYYY-MM-DD'; null = open (ongoing)
 */

/** An open bound. Both ends use it, so the intent reads at the call site. */
export const OPEN = null;

/**
 * Builds a period, normalizing both bounds.
 *
 * NORMALIZATION IS LOSSY, deliberately: a bound `toDateStr` cannot read becomes an open bound.
 * That is safe for a well-formed period but it is NOT a validation — an unreadable end date would
 * silently read as "ongoing". Judge raw input with `unreadableBounds` BEFORE constructing, which
 * is what `validateCoverage` does.
 *
 * @param {string|null|undefined} start
 * @param {string|null|undefined} end
 * @returns {EffectivePeriod} frozen — a value object has no mutators
 */
export function createEffectivePeriod(start, end) {
  return Object.freeze({
    start: toDateStr(start),
    end: toDateStr(end),
  });
} // createEffectivePeriod()

/**
 * Is the period in force on the given date? Both bounds are inclusive.
 *
 * @param {EffectivePeriod} period
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {boolean}
 */
export function includes(period, asOf) {
  return isActiveAsOf(asOf, period.start, period.end);
} // includes()

/**
 * Has the period not started yet as of the given date?
 *
 * Reads the as-of on the same strict terms as `includes`, so the two agree about what a date is.
 * A raw comparison happens to give the right answer for an instant at THIS bound — appending a
 * time only makes the string larger, which is the direction a start test wants — and that is luck
 * rather than a property to rely on: the same input is off by a day at the end bound.
 *
 * @param {EffectivePeriod} period
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {boolean}
 */
export function startsAfter(period, asOf) {
  const asOfDate = toAsOfDate(asOf);

  return asOfDate !== null && period.start !== null && asOfDate < period.start;
} // startsAfter()

/**
 * Do two periods share at least one date?
 *
 * Two ranges intersect unless one ends strictly before the other starts — which also gives the
 * right answer for open bounds, since an open end can never precede anything.
 *
 * @param {EffectivePeriod} period
 * @param {EffectivePeriod} other
 * @returns {boolean}
 */
export function overlaps(period, other) {
  const endsBefore = (a, b) => a.end !== null && b.start !== null && a.end < b.start;

  return !endsBefore(period, other) && !endsBefore(other, period);
} // overlaps()

/**
 * Is the period ongoing — no end bound?
 *
 * @param {EffectivePeriod} period
 * @returns {boolean}
 */
export function isOpenEnded(period) {
  return period.end === null;
} // isOpenEnded()

/**
 * The period's own invariant: an end bound cannot precede its start.
 *
 * Reported rather than thrown, because the caller is a form validating as the user types and a
 * half-entered period is an ordinary state, not an exception.
 *
 * @param {EffectivePeriod} period
 * @returns {boolean}
 */
export function endsBeforeStart(period) {
  return period.start !== null && period.end !== null && period.end < period.start;
} // endsBeforeStart()

/**
 * Which of the RAW bounds cannot be read as a calendar date.
 *
 * Answered on raw input because `createEffectivePeriod` has already collapsed the distinction:
 * once normalized, an unreadable end and a genuinely open one are both null. Callers that accept
 * outside input must ask this first — see `validateCoverage`, and `Coverage`'s note on why a
 * silently-open end date is the expensive failure here.
 *
 * @param {*} rawStart
 * @param {*} rawEnd
 * @returns {boolean} true when either bound was supplied but is unreadable
 */
export function unreadableBounds(rawStart, rawEnd) {
  const isUnreadable = (bound) => {
    return bound !== null && bound !== undefined && toDateStr(bound) === null;
  };

  return isUnreadable(rawStart) || isUnreadable(rawEnd);
} // unreadableBounds()

/**
 * Value equality — two periods are the same period when both bounds match.
 *
 * @param {EffectivePeriod} period
 * @param {EffectivePeriod} other
 * @returns {boolean}
 */
export function equals(period, other) {
  return period.start === other.start && period.end === other.end;
} // equals()
